import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseResponse, ContactUsModel } from '../shared';
import { environment } from 'src/environments/environment';
import { map, tap } from 'rxjs';
import camelcaseKeys from 'camelcase-keys';
import { AppUrls, InsideNavigateToUrl } from './routing.state';
import { UrlSerializer } from '@angular/router';
import { Store } from '@ngxs/store';
import { NavController } from '@ionic/angular';
import { AuthState, AuthWithToken } from './auth.state';
import { Preferences } from '@capacitor/preferences';
import { ScreenOrientation } from '@capacitor/screen-orientation';

interface LangResponse extends BaseResponse {
	data: {
		title: string;
		title_native: string;
		language_id: string;
		languages_code: string;
	}[];
}

interface LangArrayResponse extends BaseResponse {
	title: string;
	title_native: string;
	language_id: string;
	data: Record<string, string>;
}

interface NotificationsResponse extends BaseResponse {
	unread_notifications_count: number;
	data: {
		notifications_id: string;
		subject: string;
		sent_to_app: string;
		body_app: string;
		body_web_short: string;
		date_read: string;
		start_date: string;
	}[];
}

export interface NotificationInfo {
	ordersId: string;
	ordersServicesId: string;
	usersNotificationsId: string;
}

export interface InfoResponse {
	data: {
		dbtv: {
			archive: {
				name: string;
				video_url: string;
				video_thumbnail: string;
			}[];
			show_type: string;
			title: string;
			poster: string;
			stream_url: string;
			subtext: string;
			footer_text: string;
		};
		currency: { countryName: string; currency_code: string }[];
		app_info: {
			ios: AppPlatformInfoSnake;
			android: AppPlatformInfoSnake;
		};
	};
}

interface AppPlatformInfoSnake {
	require_version: string;
	require_build: string;
	version: string;
	build: string;
	store_url: string;
}

export interface AuthorizeCredentialsResponse extends BaseResponse {
	url: string;
	usd: {
		api_login_id: string;
		public_client_key: string;
	};
	cad: {
		api_login_id: string;
		public_client_key: string;
	};
}

export interface CheckSurveyResponse extends BaseResponse {
	surveys_id: string;
}

export interface GetSurveyResponse extends BaseResponse {
	surveys_id: string;
	title: string;
	comments: string;
	comments2: string;
}

export interface RateAppResponse extends BaseResponse {
	android_url: string;
	ios_url: string;
}

interface LangData {
	lang: string;
	ttl: number;
	createdAt: number;
	langData: Record<string, string>;
}

@Injectable({
	providedIn: 'root',
})
export class AppFeaturesService {
	constructor(private http: HttpClient, private urlSerializer: UrlSerializer, private store: Store, private navController: NavController) {}

	inAppNavigationHandler(url: string) {
		if (!url.includes(environment.dancebugUrl)) {
			throw Error('Invalid url');
			return;
		}
		const slug = url.split(environment.dancebugUrl).pop();
		if (!slug?.trim()) {
			return;
		}
		if (Object.values(AppUrls).some((url) => slug.includes(url))) {
			this.store
				.dispatch(new AuthWithToken())
				.pipe(
					tap(async () => {
						const authState = this.store.selectSnapshot(AuthState.state);
						if (!authState?.token) {
							return;
						}

						this.store.dispatch(new InsideNavigateToUrl(slug));
					})
				)
				.subscribe();
			return;
		}
	}

	async screenLock(throwsError = false) {
		return ScreenOrientation.lock({ orientation: 'portrait' })
			.then(() => console.log('Screen Orientation lock'))
			.catch((err) => {
				console.error('Failed to lock orientation', err);
				if (throwsError) {
					throw err;
				}
			});
	}

	screenUnlock() {
		ScreenOrientation.unlock()
			.then(() => console.log('Screen Orientation unlock'))
			.catch((err) => console.error('Failed to unlock orientation', err));
	}

	// async getAppStartCount(): Promise<string | null> {
	// 	const year = new Date().getFullYear();
	// 	const { value } = await Preferences.get({ key: `dancebug_app_counter_${year}` });

	// 	return value;
	// }

	// so the flow is:
	// 1. App starts
	// 2. We check the app start count and increase it by 1
	// 3. IF `challengeAgain` is TRUE => trigger suvey
	// 													FALSE => If count is equal to 3, 10, 50 => trigger survey
	// 4. If survey canceled we modify app start counter, marking `challengeAgain` as `true`
	// 4. If survey completed we modify app start counter, marking `challengeAgain` as `false`

	// async increaseAppStartCount(): Promise<number> {
	// 	const year = new Date().getFullYear();
	// 	const count = (await this.getAppStartCount()) || '0';
	// 	const countNumber = parseInt(count) + 1;

	// 	const { value } = await Preferences.get({ key: `dancebug_app_counter_${year}` });

	// 	const data = { year: year, count: countNumber, challengeAgain: false };

	// 	await Preferences.set({ key: `dancebug_app_counter_${year}`, value: countNumber.toString() });

	// 	return countNumber;
	// }

	async getLangTags(): Promise<LangData | undefined> {
		const { value } = await Preferences.get({ key: `dancebug_lang_tags` });

		if (value === null) return;

		const data: LangData = JSON.parse(value);

		if (data.createdAt + data.ttl < Date.now()) {
			return;
		}

		return data;
	}

	async setLangTags(lang: string, langData: Record<string, string>): Promise<void> {
		const data: LangData = { lang: lang, ttl: 604800000, createdAt: Date.now(), langData: langData }; // 60 * 60 * 24 * 7 * 1000 = 604800000 (one week in JS milliseconds)
		await Preferences.set({ key: `dancebug_lang_tags`, value: JSON.stringify(data) });
	}

	async getCurrentLang() {
		const { value } = await Preferences.get({ key: `dancebug_lang` });
		return value;
	}

	async setCurrentLang(lang: string) {
		await Preferences.set({ key: 'dancebug_lang', value: lang });
	}

	getLangPresets() {
		const request = this.http.get<LangResponse>(`${environment.apiUrl}/system/language/app`);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	getAuthorizeCredentials() {
		const request = this.http.get<AuthorizeCredentialsResponse>(`${environment.apiUrl}/authorize/credentials`);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	getLang(lang: string) {
		const request = this.http.get<LangArrayResponse>(`${environment.apiUrl}/system/language/app/${lang}`);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: false })),
			tap(() => this.setCurrentLang(lang))
		);
	}

	getNotifications(getList: boolean) {
		const urlParam = getList ? '' : '/active';
		return this.http
			.get<NotificationsResponse>(`${environment.apiUrl}/notifications` + urlParam)
			.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	contactUs(contactData: ContactUsModel, reCaptchaToken: string) {
		const formData = new FormData();
		formData.append('subject', contactData.subject);
		formData.append('requester_name', contactData.name);
		formData.append('requester_email', contactData.email);
		formData.append('comment_body', contactData.message);
		formData.append('recaptcha_response', reCaptchaToken);

		const request = this.http.post<BaseResponse>(`${environment.apiUrl}/contact-us`, formData);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	registerDevice(usersId: string, deviceToken: string, deviceId: string, notifications: boolean) {
		const formData = new FormData();
		formData.append('device_token', deviceToken);
		formData.append('device_id', deviceId);
		formData.append('enable_push_notify', notifications ? '1' : '0');
		return this.http.post<void>(`${environment.apiUrl}/users/${usersId}/update-notifications`, formData);
	}

	getInfo(field: keyof InfoResponse['data']) {
		const params: HttpParams = new HttpParams().append('get_fields', field);
		return this.http.get<InfoResponse>(`${environment.apiUrl}/info`, { params }).pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	// checkSurvey() {
	// 	return this.http
	// 		.get<CheckSurveyResponse>(`${environment.apiUrl}/surveys/${environment.appRatingSurveyId}/check`)
	// 		.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	// }

	getSurvey(surveyId: number) {
		return this.http
			.get<GetSurveyResponse>(`${environment.apiUrl}/surveys/${surveyId}`)
			.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	rateApp(surveyId: number, stars: number, comments: string, platform: string, appVersion: string, cancel: boolean) {
		const formData = new FormData();

		formData.append('cancel', cancel ? '1' : '0');

		formData.append('comments', comments);
		formData.append('stars', stars.toString());
		formData.append('surveysID', surveyId.toString());

		formData.append('platform', platform);
		formData.append('app_version', appVersion);

		const request = this.http.post<RateAppResponse>(`${environment.apiUrl}/surveys`, formData);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}
}
